<script setup lang="ts">import { computed as _computed } from 'vue';

import RsFilePassword from "./FilePassword.vue";

import { Metadata } from "@/models/Metadata";

import { useMetadataStore } from "@/stores/metadata-store";

import { isPasswordProtected } from "@/services/UtilitiesService";

import type Alias from "@/models/Alias";

withDefaults(defineProps<{
  brand: string;
  casename: string;
  casefile?: string;
  file: Metadata;
}>(), {  })

const hasFiles = _computed(() => {
  return subFiles.value.length > 0;
});

const subFiles = _computed(() => {
  if (__props.casefile !== __props.file.fileReference) {
    return [];
  }

  return useMetadataStore().filteredFilesInCurrentFolder.filter((f) => f.parent === __props.casefile);
});

const duplicateCount = _computed(() => {
  const duplicates: Metadata[] = useMetadataStore().files.filter(
    (f: Metadata) => (f.aliases || []).findIndex((a: Alias) => a.parent === __props.casefile) > -1,
  );

  return duplicates.length;
});
</script>
<template>
  <div class="tw-size-full">
    <nav class="tw-sticky tw-top-0 tw-bg-gray-900 tw-px-3 tw-py-1">
      <p class="tw-ml-1 tw-text-base tw-text-white">
        Viewing contents of:
        <strong>{{ file.displayName }}</strong>
      </p>
    </nav>
    <div>
      <div class="tw-bg-gray-100" style="margin: 1rem 2rem; padding: 2rem">
        <div v-if="isPasswordProtected(file)" class="tw-p-3">
          <RsFilePassword :brand="brand" :casename="casename" :file="file" />
        </div>
        <table v-else-if="hasFiles" class="tw-table-dark">
          <thead>
            <tr>
              <th>Name</th>
              <th>Full name</th>
              <th>Classification</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(subFile, index) in subFiles" :key="`${file.fileReference}-${index}`">
              <tr :class="{ 'tw-bg-gray-800': index % 2 === 0 }">
                <th scope="row" :title="subFile.edxFilename">
                  <router-link :to="`/${brand}/${casename}/${subFile.fileReference}`">{{
                    subFile.displayName
                  }}</router-link>
                </th>
                <td>{{ subFile.edxFilename }}</td>
                <td>{{ subFile.aiClassification }}</td>
                <td>{{ subFile.fileType }}</td>
              </tr>
            </template>
          </tbody>
        </table>
        <div v-else-if="duplicateCount > 0">
          <h2 class="tw-mb-4">
            <small>This archive looks empty,</small>
            however, it does contain {{ duplicateCount }} duplicate file{{ duplicateCount > 1 ? "s" : "" }} which
            {{ duplicateCount > 1 ? "are" : "is" }} hidden at the moment.
          </h2>
          <p>Remove the hide duplicates filter to see the files in this archive</p>
        </div>
        <div v-else>
          <h2 class="tw-mb-4"><small>This archive looks empty,</small> don't worry though</h2>
          <p>
            The files could still be in the process of being loaded and will be displayed here as they are added, no
            need to refresh the page.
          </p>
          <p>
            However, if this archive is persistently empty then please raise it with your
            <strong>Technical Support</strong> team for assistance.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
